import React, { useContext, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Alert, Box, Button, Card, Chip, CircularProgress, Divider, Grid, Switch, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import BusinessIcon from '@mui/icons-material/Business';
import PageHeader from '../../../sharedModules/layout/PageHeader';
import { SnackBarContext } from '../../../sharedModules/form/contexts/SnackBarContext';
import { useCollectionClient } from '../hooks/useCollectionClient';
import { useCollectionClientNotes } from '../hooks/useCollectionClientNotes';
import { useUpdateCollectionClient } from '../hooks/useUpdateCollectionClient';
import { CollectionInvoiceDocument, CollectionInvoicePdf } from '../model';
import { formatDollar } from '../utils/format';
import ClientInvoicesTable from '../components/ClientInvoicesTable';
import ClientEmailHistoryTable from '../components/ClientEmailHistoryTable';
import CollectionClientNotesModal from '../components/CollectionClientNotesModal';
import CollectionClientBackupEmailCard from '../components/CollectionClientBackupEmailCard';
import CollectionClientContactsCard from '../components/CollectionClientContactsCard';
import { useToggleCollectionClientContactEmail } from '../hooks/useToggleCollectionClientContactEmail';

const formatNoteTimestamp = (iso: string): string =>
  new Date(iso).toLocaleString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
    {children}
  </Typography>
);

const DetailField: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <Box>
    <Typography variant="caption" color="text.secondary" display="block">
      {label}
    </Typography>
    {children}
  </Box>
);

const formatAddressLine = (...parts: (string | null | undefined)[]): string | null => {
  const joined = parts.filter(Boolean).join(', ');
  return joined || null;
};

const formatStreetLine = (line1: string | null, line2: string | null, line3: string | null): string | null =>
  formatAddressLine(line1, line2, line3);

const formatCityStateZip = (city: string | null, state: string | null, zip: string | null): string | null => {
  const cityState = [city, state].filter(Boolean).join(', ');
  return [cityState, zip].filter(Boolean).join(' ') || null;
};

const hasAnyValue = (...parts: (string | null | undefined)[]): boolean => parts.some(Boolean);

const AddressValue: React.FC<{ street: string | null; cityLine: string | null; extraLine?: string | null }> = ({
  street,
  cityLine,
  extraLine,
}) => (
  <>
    {street && (
      <Typography variant="body2" color="text.primary">
        {street}
      </Typography>
    )}
    {cityLine && (
      <Typography variant="body2" color="text.primary">
        {cityLine}
      </Typography>
    )}
    {extraLine && (
      <Typography variant="body2" color="text.primary">
        {extraLine}
      </Typography>
    )}
    {!street && !cityLine && !extraLine && (
      <Typography variant="body2" color="text.primary">
        —
      </Typography>
    )}
  </>
);

const CollectionClientDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const clientId = Number(id);
  const history = useHistory();
  const queryClient = useQueryClient();
  const { showErrorSnackBar, showSuccessSnackBar } = useContext(SnackBarContext);

  const { data, isLoading, isError } = useCollectionClient(clientId);
  const client = data?.data ?? null;
  const { data: notesData } = useCollectionClientNotes(clientId);
  const notes = notesData?.data ?? [];
  const { updateClient, isLoading: isToggling } = useUpdateCollectionClient();
  const { toggleContactEmail, isLoading: isTogglingContactEmail } = useToggleCollectionClientContactEmail();

  const [active, setActive] = useState(false);
  const [documents, setDocuments] = useState<CollectionInvoiceDocument[]>([]);
  const [notesOpen, setNotesOpen] = useState(false);

  useEffect(() => {
    if (client) {
      setActive(client.active);
      setDocuments(client.invoice_documents);
    }
  }, [client]);

  const handleToggleActive = async (checked: boolean) => {
    if (!client) return;
    const previous = active;
    setActive(checked);
    try {
      await updateClient({ id: clientId, active: checked });
      showSuccessSnackBar?.(`Client ${checked ? 'enabled' : 'disabled'} for scheduled emails.`);
    } catch (error: any) {
      setActive(previous);
      const serverMessage = error?.response?.data?.errors?.[0] || error?.response?.data?.error;
      showErrorSnackBar?.(serverMessage || 'Failed to update client status.');
    }
  };

  const handleUploaded = (applyTo: string, pdf: CollectionInvoicePdf) => {
    setDocuments((prev) => prev.map((d) => (d.apply_to === applyTo ? { ...d, has_pdf: true, pdf } : d)));
    showSuccessSnackBar?.('Invoice PDF uploaded.');
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError || !client) {
    return (
      <Grid container>
        <Grid item xs={12} sx={{ mt: 2 }}>
          <Alert severity="error">Failed to load client.</Alert>
        </Grid>
        <Grid item xs={12} sx={{ mt: 2 }}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => history.push('/collections/clients')}>
            Back to Clients
          </Button>
        </Grid>
      </Grid>
    );
  }

  const notEmailable = !client.emailable;
  const previewNotes = notes.slice(0, 5);

  const billingStreet = formatStreetLine(client.billing_address_1, client.billing_address_2, client.billing_address_3);
  const billingCityLine = formatCityStateZip(client.billing_city, client.billing_state, client.billing_zip);

  const physicalStreet = formatStreetLine(
    client.physical_address_1,
    client.physical_address_2,
    client.physical_address_3,
  );
  const physicalCityLine = formatCityStateZip(client.physical_city, client.physical_state, null);
  const physicalExtraLine = formatAddressLine(client.physical_county, client.physical_country);
  const hasPhysicalAddress = hasAnyValue(
    client.physical_address_1,
    client.physical_address_2,
    client.physical_address_3,
    client.physical_city,
    client.physical_state,
    client.physical_county,
    client.physical_country,
  );
  const companyEmails = client.servman_emails;
  const recipientEmails = client.emails ?? [];

  const handleToggleContactEmail = async (conIdNo: string, action: 'add' | 'remove') => {
    try {
      await toggleContactEmail({
        id: clientId,
        con_id_no: conIdNo,
        action,
        location: client.location,
      });
      showSuccessSnackBar?.(
        action === 'add' ? 'Contact added to collection emails.' : 'Contact removed from collection emails.',
      );
    } catch (error: unknown) {
      const err = error as { response?: { data?: { errors?: string[]; error?: string } } };
      const message =
        err?.response?.data?.errors?.[0] || err?.response?.data?.error || 'Failed to update collection emails.';
      showErrorSnackBar?.(message);
      throw error;
    }
  };

  return (
    <Grid container>
      <Grid item xs={12}>
        <PageHeader mainTitle={`Collections — ${client.company}`} titleIcon={<BusinessIcon />} />
      </Grid>

      <Grid
        item
        xs={12}
        sx={{ mt: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}
      >
        <Button startIcon={<ArrowBackIcon />} size="small" onClick={() => history.push('/collections/clients')}>
          Back to Clients
        </Button>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {client.tone && <Chip label={client.tone} size="small" />}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="body2">Active</Typography>
            <Switch
              checked={notEmailable ? false : active}
              onChange={(e) => handleToggleActive(e.target.checked)}
              color="primary"
              size="small"
              disabled={isToggling || notEmailable}
              inputProps={{ 'aria-label': `Active for ${client.company}` }}
            />
          </Box>
        </Box>
      </Grid>

      <Grid item xs={12} sx={{ mt: 2 }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '65fr 35fr' },
            gap: 2,
            alignItems: 'stretch',
          }}
        >
        <Card sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
          <SectionLabel>Company Info</SectionLabel>
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <DetailField label="Company">
                  <Typography variant="body2">{client.company}</Typography>
                </DetailField>
              </Grid>
              <Grid item xs={6}>
                <DetailField label="Bill Code">
                  <Typography variant="body2">{client.bill_code}</Typography>
                </DetailField>
              </Grid>
            </Grid>

            {client.com_or_res && (
              <DetailField label="Company Type">
                <Typography variant="body2">{client.com_or_res === 'C' ? 'Commercial' : 'Residential'}</Typography>
              </DetailField>
            )}

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <DetailField label="Billing Company">
                  <Typography variant="body2">{client.billing_company || '—'}</Typography>
                </DetailField>
              </Grid>
              <Grid item xs={6}>
                <DetailField label="Billing Address">
                  <AddressValue street={billingStreet} cityLine={billingCityLine} />
                </DetailField>
              </Grid>
            </Grid>

            {(hasPhysicalAddress || client.primary_phone || client.secondary_phone) && (
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <DetailField label="Physical Address">
                    <AddressValue street={physicalStreet} cityLine={physicalCityLine} extraLine={physicalExtraLine} />
                  </DetailField>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {client.primary_phone && (
                      <DetailField label="Primary Phone">
                        <Typography variant="body2">{client.primary_phone}</Typography>
                      </DetailField>
                    )}
                    {client.secondary_phone && (
                      <DetailField label="Secondary Phone">
                        <Typography variant="body2">{client.secondary_phone}</Typography>
                      </DetailField>
                    )}
                  </Box>
                </Grid>
              </Grid>
            )}

            {client.fax && (
              <DetailField label="Fax">
                <Typography variant="body2">{client.fax}</Typography>
              </DetailField>
            )}

            <DetailField label="Contacts">

              <CollectionClientContactsCard
                contacts={client.contacts}
                recipientEmails={recipientEmails}
                onToggleContact={handleToggleContactEmail}
                disabled={isTogglingContactEmail}
              />
              <Box sx={{ mb: 1.5 }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Collection emails sent to:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.25 }}>
                  {companyEmails.length > 0 ? (
                    companyEmails.map((email) => <Chip key={email} label={email} size="small" />)
                  ) : (
                    <Chip
                      label={client.backup_email ? `Backup: ${client.backup_email}` : 'No client email'}
                      size="small"
                      color="error"
                      variant="outlined"
                    />
                  )}
                </Box>
              </Box>
            </DetailField>
            <CollectionClientBackupEmailCard
              client={client}
              onSaved={() => {
                void queryClient.invalidateQueries({ queryKey: [`/collections/clients/${clientId}`, undefined] });
              }}
            />
          </Box>

          <Divider sx={{ my: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <DetailField label="Past Due">
                <Typography
                  variant="body2"
                  sx={{ color: client.total_past_due > 0 ? 'error.main' : 'text.primary', fontWeight: 500 }}
                >
                  {formatDollar(client.total_past_due)}
                </Typography>
              </DetailField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <DetailField label="Invoices">
                <Typography variant="body2">{client.invoice_count}</Typography>
              </DetailField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <DetailField label="Worst Aging">
                <Typography variant="body2">{client.worst_aging_days} days</Typography>
              </DetailField>
            </Grid>
          </Grid>
        </Card>

        <Card sx={{ p: 2, height: '100%' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <SectionLabel>Recent Notes (Last 5 notes)</SectionLabel>
            <Button size="small" onClick={() => setNotesOpen(true)}>
              View all / Add note
            </Button>
          </Box>
          {previewNotes.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No notes yet.
            </Typography>
          ) : (
            previewNotes.map((note) => (
              <Box key={note.id} sx={{ mb: 1 }}>
                <Typography variant="caption" color="primary">
                  {formatNoteTimestamp(note.display_at)} ·{' '}
                  {note.imported_from_servman ? note.added_by || 'Servman' : note.user_name || 'System'}
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {note.body}
                </Typography>
              </Box>
            ))
          )}
        </Card>
        </Box>
      </Grid>

      <Grid item xs={12} sx={{ mt: 2.5 }}>
        <SectionLabel>Aging Invoices</SectionLabel>
        <ClientInvoicesTable
          documents={documents}
          location={client.location}
          acctNo={client.acct_no}
          onUploaded={handleUploaded}
        />
      </Grid>

      <Grid item xs={12} sx={{ mt: 2.5 }}>
        <SectionLabel>Email History</SectionLabel>
        <ClientEmailHistoryTable location={client.location} clientId={clientId} />
      </Grid>

      <CollectionClientNotesModal
        open={notesOpen}
        onClose={() => setNotesOpen(false)}
        clientId={clientId}
        company={client.company}
        onNotesChanged={() => {
          void queryClient.invalidateQueries({ queryKey: [`/collections/clients/${clientId}/notes`, undefined] });
        }}
      />
    </Grid>
  );
};

export default CollectionClientDetailPage;
